import { useParams, useNavigate } from 'react-router-dom';
import { config } from "../../data/config";
import { getCookies } from "cookies-next";
import axios from "axios";
import { useEffect, useState, useRef, useCallback } from "react";
import BreadCrump from "../tools/breadcrump";
import HeaderOfPage from "../tools/headerOfPage";
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const inputstyle = "py-2.5 px-3 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none bg-white";

const defaultMarketplace = {
    title: '',
    userId: '',
    description: '',
    mapLocation: '',
    active: true,
    expiredDate: '',
    address: '',
    phone: '',
    phone2: '',
    fax: '',
    email: '',
    website: '',
    whatsapp: '',
    instagram: '',
    eitaa: '',
    igap: '',
    rubika: '',
    discord: '',
    x: '',
    facebook: '',
    pinterest: '',
    telegram: '',
    telegramChannel: '',
    tiktok: '',
    youtube: '',
    aparat: '',
    keyword: '',
    commission: 20,
    province: '',
};

export default function EditMarketplace() {
    const { t } = useTranslation();
    const { Id } = useParams();
    const navigate = useNavigate();
    const isNew = Id === 'new' || Id === undefined;
    const invalidId = !isNew && (Id === 'undefined' || !Id);
    let cookie = getCookies();
    const [data, setData] = useState<any>({ ...defaultMarketplace });
    const [loading, setLoading] = useState(!isNew && !invalidId);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [userSearchResults, setUserSearchResults] = useState<any[]>([]);
    const [selectedUserDisplay, setSelectedUserDisplay] = useState('');
    const [userSearchOpen, setUserSearchOpen] = useState(false);
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const userSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const userSearchContainerRef = useRef<HTMLDivElement>(null);

    const breadcrump = [
        { name: 'Dashboard', url: '/dashboard' },
        { name: 'Marketplace Management', url: '/dashboard/Marketplace' },
        { name: 'List', url: '/dashboard/Marketplace/list' },
        { name: isNew ? t('New Marketplace') : t('Edit Marketplace'), url: null },
    ];

    const load = async () => {
        if (isNew || invalidId) return;
        try {
            const res = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/marketplace/${Id}`,
                headers: { Authorization: 'Bearer ' + cookie['token'] }
            });
            const msg = res.data.message || res.data;
            const normalized = { ...defaultMarketplace, ...msg, expiredDate: msg.expiredDate ? msg.expiredDate.slice(0, 10) : '' };
            const uid = typeof msg.userId === 'object' && msg.userId?._id ? msg.userId._id : (msg.userId || msg.user?._id);
            if (uid) normalized.userId = typeof uid === 'string' ? uid : (uid?.toString?.() || '');
            setData(normalized);
            if (uid) {
                try {
                    const idStr = typeof uid === 'string' ? uid : (uid?.toString?.() || '');
                    const uRes = await axios({
                        method: 'get',
                        url: `${config.url}/v1/dashboard/users/${idStr}`,
                        headers: { Authorization: 'Bearer ' + cookie['token'] }
                    });
                    const u = uRes.data.message || uRes.data;
                    if (u) setSelectedUserDisplay([u.name, u.lastname].filter(Boolean).join(' ') + (u.email ? ` - ${u.email}` : ''));
                } catch (_) {}
            }
        } catch (error) {
            Swal.fire(t("Error"), t("Could not load marketplace."), "error");
        } finally {
            setLoading(false);
        }
    };

    const searchUsers = useCallback(async (q: string) => {
        if (!q.trim()) {
            setUserSearchResults([]);
            return;
        }
        setUserSearchLoading(true);
        try {
            const res = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/users?search=${encodeURIComponent(q.trim())}&page=1`,
                headers: { Authorization: 'Bearer ' + cookie['token'] }
            });
            const list = res.data.message;
            const docs = list?.docs ?? list ?? [];
            setUserSearchResults(Array.isArray(docs) ? docs : []);
        } catch (_) {
            setUserSearchResults([]);
        } finally {
            setUserSearchLoading(false);
        }
    }, []);

    useEffect(() => {
        if (userSearchTimeoutRef.current) clearTimeout(userSearchTimeoutRef.current);
        if (!userSearchQuery.trim()) {
            setUserSearchResults([]);
            setUserSearchOpen(!!userSearchQuery);
            return;
        }
        userSearchTimeoutRef.current = setTimeout(() => {
            searchUsers(userSearchQuery);
            setUserSearchOpen(true);
        }, 300);
        return () => {
            if (userSearchTimeoutRef.current) clearTimeout(userSearchTimeoutRef.current);
        };
    }, [userSearchQuery, searchUsers]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (userSearchContainerRef.current && !userSearchContainerRef.current.contains(e.target as Node)) {
                setUserSearchOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectUser = (user: any) => {
        const id = user.id || user._id;
        if (id) {
            update('userId', id);
            setSelectedUserDisplay([user.name, user.lastname].filter(Boolean).join(' ') + (user.email ? ` - ${user.email}` : ''));
            setUserSearchQuery('');
            setUserSearchResults([]);
            setUserSearchOpen(false);
        }
    };

    const clearUser = () => {
        update('userId', '');
        setSelectedUserDisplay('');
        setUserSearchQuery('');
        setUserSearchResults([]);
        setUserSearchOpen(false);
    };

    useEffect(() => {
        if (isNew || invalidId) {
            if (invalidId) navigate('/dashboard/Marketplace/list', { replace: true });
            setLoading(false);
        } else {
            load();
        }
    }, [Id, isNew, invalidId]);

    const save = async () => {
        if (invalidId) return;
        try {
            const payload = { ...data };
            if (payload.expiredDate === '') delete payload.expiredDate;
            if (isNew) {
                await axios({
                    method: 'post',
                    url: config.url + '/v1/dashboard/marketplace',
                    data: payload,
                    headers: { Authorization: 'Bearer ' + cookie['token'] }
                });
                Swal.fire(t("Saved"), t("Marketplace created."), "success");
            } else {
                await axios({
                    method: 'put',
                    url: config.url + '/v1/dashboard/marketplace/' + Id,
                    data: payload,
                    headers: { Authorization: 'Bearer ' + cookie['token'] }
                });
                Swal.fire(t("Saved"), t("Marketplace updated."), "success");
            }
            navigate('/dashboard/Marketplace/list');
        } catch (error: any) {
            const msg = error.response?.data?.message || t("Could not save.");
            Swal.fire(t("Error"), msg, "error");
        }
    };

    const update = (key: string, value: any) => setData((prev: any) => ({ ...prev, [key]: value }));

    if (invalidId || loading) return <div className="p-4">{invalidId ? null : t("Loading...")}</div>;

    return (
        <div className="text-left bg-white p-3 m-4 directions mb-5">
            <BreadCrump data={breadcrump} />
            <HeaderOfPage name={isNew ? t("New Marketplace") : t("Edit Marketplace")} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic info */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">{t("Basic Info")}</h3>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Title")} *</label>
                        <input className={inputstyle} value={data.title} onChange={(e) => update('title', e.target.value)} placeholder={t("Title")} />
                    </div>
                    <div ref={userSearchContainerRef} className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("UserId")}</label>
                        {selectedUserDisplay ? (
                            <div className="flex items-center gap-2">
                                <div className={`${inputstyle} flex-1 bg-gray-50`} dir="ltr">
                                    {selectedUserDisplay}
                                </div>
                                <button type="button" onClick={clearUser} className="py-2.5 px-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100" title={t("Clear")}>
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <>
                                <input
                                    className={inputstyle}
                                    dir="ltr"
                                    value={userSearchQuery}
                                    onChange={(e) => setUserSearchQuery(e.target.value)}
                                    onFocus={() => userSearchResults.length > 0 && setUserSearchOpen(true)}
                                    placeholder={t("Search by name, lastname or email")}
                                    autoComplete="off"
                                />
                                {userSearchLoading && <span className="absolute left-3 top-9 text-xs text-gray-500">{t("Loading...")}</span>}
                                {userSearchOpen && userSearchResults.length > 0 && (
                                    <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                                        {userSearchResults.map((user: any) => {
                                            const id = user.id || user._id;
                                            const label = [user.name, user.lastname].filter(Boolean).join(' ') + (user.email ? ` - ${user.email}` : '');
                                            return (
                                                <li
                                                    key={id}
                                                    onClick={() => selectUser(user)}
                                                    className="px-3 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-0 text-sm"
                                                >
                                                    {label || id}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                                {userSearchOpen && !userSearchLoading && userSearchQuery.trim() && userSearchResults.length === 0 && (
                                    <div className="absolute z-10 w-full mt-1 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow text-sm text-gray-500">
                                        {t("No results found")}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Description")}</label>
                        <textarea className={inputstyle} rows={4} value={data.description} onChange={(e) => update('description', e.target.value)} placeholder={t("Description")} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Map Location")}</label>
                        <input className={inputstyle} dir="ltr" value={data.mapLocation} onChange={(e) => update('mapLocation', e.target.value)} placeholder="lat,lng or link" />
                    </div>
                    <div className="flex items-center gap-2">
                        <input type="checkbox" id="active" checked={!!data.active} onChange={(e) => update('active', e.target.checked)} />
                        <label htmlFor="active" className="text-sm font-medium text-gray-700">{t("Active")}</label>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Expired Date")}</label>
                        <input className={inputstyle} type="date" dir="ltr" value={data.expiredDate || ''} onChange={(e) => update('expiredDate', e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Keyword")}</label>
                        <input className={inputstyle} value={data.keyword} onChange={(e) => update('keyword', e.target.value)} placeholder={t("Keyword")} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Commission")} (%)</label>
                        <input className={inputstyle} type="number" min={0} max={100} dir="ltr" value={data.commission ?? 20} onChange={(e) => update('commission', parseInt(e.target.value, 10) || 20)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Province")}</label>
                        <input className={inputstyle} value={data.province} onChange={(e) => update('province', e.target.value)} placeholder={t("Province")} />
                    </div>
                </div>

                {/* Contact & Social */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">{t("Contact")}</h3>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Address")}</label>
                        <textarea className={inputstyle} rows={2} value={data.address} onChange={(e) => update('address', e.target.value)} placeholder={t("Address")} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("Phone")}</label>
                            <input className={inputstyle} dir="ltr" value={data.phone} onChange={(e) => update('phone', e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("Phone 2")}</label>
                            <input className={inputstyle} dir="ltr" value={data.phone2} onChange={(e) => update('phone2', e.target.value)} />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Fax")}</label>
                        <input className={inputstyle} dir="ltr" value={data.fax} onChange={(e) => update('fax', e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Email")}</label>
                        <input className={inputstyle} type="email" dir="ltr" value={data.email} onChange={(e) => update('email', e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("Website")}</label>
                        <input className={inputstyle} type="url" dir="ltr" value={data.website} onChange={(e) => update('website', e.target.value)} placeholder="https://" />
                    </div>

                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mt-6">{t("Social Networks")}</h3>
                    {[
                        { key: 'whatsapp', label: 'WhatsApp' },
                        { key: 'instagram', label: 'Instagram' },
                        { key: 'eitaa', label: 'Eitaa' },
                        { key: 'igap', label: 'iGap' },
                        { key: 'rubika', label: 'Rubika' },
                        { key: 'discord', label: 'Discord' },
                        { key: 'x', label: 'X (Twitter)' },
                        { key: 'facebook', label: 'Facebook' },
                        { key: 'pinterest', label: 'Pinterest' },
                        { key: 'telegram', label: 'Telegram' },
                        { key: 'telegramChannel', label: 'Telegram Channel' },
                        { key: 'tiktok', label: 'TikTok' },
                        { key: 'youtube', label: 'YouTube' },
                        { key: 'aparat', label: 'Aparat' },
                    ].map(({ key, label }) => (
                        <div key={key}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                            <input className={inputstyle} dir="ltr" value={data[key] || ''} onChange={(e) => update(key, e.target.value)} placeholder={`${label} URL`} />
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-6 flex gap-3">
                <button type="button" onClick={save} className="py-2.5 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                    {t("Save")}
                </button>
                <button type="button" onClick={() => navigate('/dashboard/Marketplace/list')} className="py-2.5 px-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium">
                    {t("Cancel")}
                </button>
            </div>
        </div>
    );
}
